import { useState, useRef, useEffect } from 'react'
import { useTranslation } from 'react-i18next';
import { FcHome, FcExpand } from 'react-icons/fc';
import { useEstablishment } from '@/contexts/EstablishmentContext';
import { LoadingSpinner } from './LoadingSpinner';

export default function CondominiumSelector() {
  const { currentEstablishment, establishments, setCurrentEstablishment, loading, getFormattedRoles } = useEstablishment();
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setSearchQuery('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isOpen]);

  const handleSelect = (establishmentId: string) => {
    const establishment = establishments.find(e => e.id === establishmentId);
    if (establishment) {
      setCurrentEstablishment(establishment);
      setIsOpen(false);
      setSearchQuery('');
    }
  };

  const filteredEstablishments = establishments.filter(
    est => 
      est.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      est.roles.some(role => role.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  // Show loading state only when actually loading establishments
  // Establishments now come from AuthContext (via /auth/me), so they're available immediately after login
  if (loading) {
    return (
      <div className="flex items-center space-x-2 px-3 py-2 text-sm bg-gray-50 rounded-lg border border-gray-200">
        <LoadingSpinner />
        <span className="text-gray-500">{t('common.loading')}</span>
      </div>
    );
  }

  if (!currentEstablishment) {
    return null;
  }

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center space-x-2 px-3 py-2 text-sm bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors duration-200 border border-gray-200"
      >
        <FcHome className="h-5 w-5" />
        <div className="text-left hidden md:block">
          <div className="font-medium text-gray-900 truncate max-w-[200px]" title={currentEstablishment.name}>
            {currentEstablishment.name}
          </div>
          <div className="text-xs text-gray-500">
            {getFormattedRoles(currentEstablishment.roles)}
          </div>
        </div>
        <FcExpand className={`h-4 w-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <div className="absolute right-0 mt-2 w-72 bg-white rounded-lg shadow-lg border border-gray-200 z-50">
          <div className="p-3 border-b border-gray-100">
            <input
              ref={searchInputRef}
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Pesquisar condomínio..."
              className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          <div className="max-h-64 overflow-y-auto">
            {filteredEstablishments.length > 0 ? (
              filteredEstablishments.map((establishment) => (
              <button
                key={establishment.id}
                onClick={() => handleSelect(establishment.id)}
                className={`w-full px-3 py-2 text-left hover:bg-gray-50 transition-colors ${
                  currentEstablishment.id === establishment.id ? 'bg-blue-50' : ''
                }`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="text-sm font-medium text-gray-900">
                      {establishment.name}
                    </div>
                    <div className="text-xs text-gray-500">
                      {getFormattedRoles(establishment.roles)}
                    </div>
                  </div>
                  {currentEstablishment.id === establishment.id && (
                    <div className="h-2 w-2 bg-blue-600 rounded-full"></div>
                  )}
                </div>
              </button>
            ))
          ) : (
            <div className="px-4 py-8 text-center text-gray-500">
              <p className="text-sm">Nenhum estabelecimento encontrado</p>
            </div>
          )}
          </div>
        </div>
      )}
    </div>
  );
}